import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip, { TooltipProps } from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import CardLayered from '@liquidweb-dev/undercurrent/CardLayered';
import CardLayeredFooter from '@liquidweb-dev/undercurrent/CardLayeredFooter';
import CircleDollarSignIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleDollarSign';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import InfoIcon from '@liquidweb-dev/undercurrent/icons/lucide/Info';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import HookedCounter from '../Form/HookedCounter';
import HookedSwitch from '../Form/HookedSwitch';

interface HookedSwitchWithTooltipProps {
	name: string;
	label: string;
	disabled?: boolean;
	tooltip?: Omit<TooltipProps, 'children'>;
}

const HookedSwitchWithTooltip = ({
	name,
	label,
	disabled,
	tooltip,
}: HookedSwitchWithTooltipProps) => {
	const labelContent = tooltip ? (
		<Stack direction="row" gap={100} alignItems="center">
			<span>{label}</span>
			<Tooltip
				arrow
				message={tooltip.message}
				placement="right"
				title={tooltip.title || 'What is this?'}
				variant="fancy"
			>
				<IconButton variant="barebones" size="large">
					<InfoIcon
						sx={{
							height: (theme) => theme.size.xS,
							width: (theme) => theme.size.xS,
						}}
					/>
				</IconButton>
			</Tooltip>
		</Stack>
	) : (
		label
	);

	return <HookedSwitch name={name} label={labelContent} disabled={disabled} />;
};

interface DomainRenewalSummaryProps {
	domain: string;
	renewalPrice: number | string;
	privacyPrice: number | string | null;
	privacyEnabled: boolean;
	isRGP: boolean;
	isExpired: boolean;
	redemptionPrice: number | string | null;
	renewalMaxYears: number;
}

const DomainRenewalSummary = ({
	domain,
	renewalPrice,
	privacyPrice,
	privacyEnabled,
	isRGP,
	isExpired,
	redemptionPrice,
	renewalMaxYears,
}: DomainRenewalSummaryProps) => {
	return (
		<CardLayered
			sx={{ backgroundColor: ({ vars }) => vars.palette.uc.warning.bg.main }}
		>
			<Card>
				<CardContent>
					<Stack sx={{ gap: 200 }}>
						<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
							Domain renewal for {domain}
						</Typography>

						<HookedCounter
							name="numberOfYears"
							label="Number of years"
							min={1}
							max={renewalMaxYears}
							required
							chipPrice={Number(renewalPrice)}
							chipProps={{
								color: 'default',
								icon: (
									<CircleDollarSignIcon
										sx={{
											[pseudoClasses.important]: {
												color: ({ vars }) =>
													vars.palette.uc.success.icon.strong,
											},
										}}
									/>
								),
							}}
						/>
						{isRGP && redemptionPrice && (
							<Stack direction="row" gap={50} justifyContent="space-between">
								<Stack direction="row" gap={100} alignItems="center">
									<Typography variant="label-lg" fontWeight={500}>
										Third Party Redemption Fee
									</Typography>
									<Tooltip
										arrow
										message="Domain redemption occurs when a domain name has passed the expired grace period. During the redemption period the domain name owner can renew their domain, but the registrar charges an additional redemption fee."
										placement="right"
										title="What is this?"
										variant="fancy"
									>
										<IconButton variant="barebones" size="large">
											<InfoIcon
												sx={{
													height: (theme) => theme.size.xS,
													width: (theme) => theme.size.xS,
												}}
											/>
										</IconButton>
									</Tooltip>
								</Stack>
								<Typography
									variant="body-sm"
									sx={{
										color: ({ vars }) => vars.palette.uc.highlight.text.main,
									}}
								>
									{formatCurrency(redemptionPrice)}
								</Typography>
							</Stack>
						)}
						{privacyPrice && (
							<Stack
								sx={{
									gap: 200,
									alignItems: 'center',
									justifyContent: 'space-between',
								}}
								direction="row"
							>
								<HookedSwitchWithTooltip
									name="privacy"
									label="WHOIS Privacy Protection"
									{...(isExpired && {
										tooltip: {
											title: 'Not available',
											message:
												'Purchasing WHOIS Privacy Protection is unavailable while the domain is expired. It can be purchased once the domain is renewed.',
										},
										disabled: true,
									})}
								/>
								<Typography
									variant="body-sm"
									sx={{
										color: ({ vars }) =>
											privacyEnabled
												? vars.palette.uc.highlight.text.main
												: vars.palette.uc.text.weak,
									}}
								>
									{formatCurrency(privacyPrice)}
								</Typography>
							</Stack>
						)}

						<HookedSwitchWithTooltip
							name="autoRenew"
							label="Turn on autorenew"
							{...(isExpired && {
								tooltip: {
									title: 'Not available',
									message:
										'Enabling auto-renew is unavailable while the domain is expired. It can be enabled once the domain is renewed.',
								},
								disabled: true,
							})}
						/>
					</Stack>
				</CardContent>
			</Card>
			<CardLayeredFooter
				sx={{ backgroundColor: ({ vars }) => vars.palette.uc.warning.bg.main }}
			>
				<Typography variant="body-sm">
					All charges are immediately billed and nonrefundable
				</Typography>
			</CardLayeredFooter>
		</CardLayered>
	);
};

export default DomainRenewalSummary;
